import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import type { TourPredicate } from "../types/tour.types";

const can =
  (resource: string, permission: string): TourPredicate =>
  (ctx) =>
    ctx.can(resource, permission);

export const feature =
  (key: string): TourPredicate =>
  (ctx) =>
    ctx.hasFeature(key);

export const allOf =
  (...predicates: TourPredicate[]): TourPredicate =>
  (ctx) =>
    predicates.every((predicate) => predicate(ctx));

export const anyOf =
  (...predicates: TourPredicate[]): TourPredicate =>
  (ctx) =>
    predicates.some((predicate) => predicate(ctx));

export const hasSideNav: TourPredicate = (ctx) => ctx.hasSideNav;

export const isDesktop: TourPredicate = (ctx) => !ctx.isMobile;

// ── Sign requests ────────────────────────────────────────────────────────────

export const canViewRequests = can(
  strings.resources.sign,
  strings.fetchPermission,
);

export const canSendRequests = can(
  strings.resources.sign,
  strings.sendPermission,
);

/** Reading the request plus sending it — what the builder flow needs. */
export const canBuildRequests = allOf(canViewRequests, canSendRequests);

/** Reading requests plus creating new ones from the list screen. */
export const canCreateRequests = allOf(
  canViewRequests,
  can(strings.resources.sign, strings.addPermission),
);

export const canUseAiTools = allOf(
  canBuildRequests,
  feature(FeatureKeys.AI_ASSISTANT),
);

// ── Templates ────────────────────────────────────────────────────────────────

export const canUseTemplates = allOf(
  feature(FeatureKeys.TEMPLATE_USE),
  can(strings.resources.template, strings.fetchPermission),
);

export const canAddTemplates = allOf(
  canUseTemplates,
  can(strings.resources.template, strings.addPermission),
);

// ── Contacts ─────────────────────────────────────────────────────────────────

export const canViewContacts = can(
  strings.resources.contacts,
  strings.fetchPermission,
);

export const canAddContacts = allOf(
  canViewContacts,
  can(strings.resources.contacts, strings.addPermission),
);

// ── Analytics ────────────────────────────────────────────────────────────────

/** Insights and Reports are the same entitlement behind two screens. */
export const canViewAnalytics = allOf(
  feature(FeatureKeys.REPORTING_ANALYTICS),
  can(strings.resources.report, strings.fetchPermission),
);

export const canViewRenewables = can(
  strings.resources.renewable,
  strings.fetchPermission,
);

// ── Web forms ────────────────────────────────────────────────────────────────

export const canUseWebForms = feature(FeatureKeys.ENVELOPE_WEB_FORMS);
